// Tactile feedback: `navigator.vibrate` where supported, degrading to a hidden
// switch-appearance <input> whose state change makes WebKit fire a Taptic tick
// (iOS Safari has no vibrate), no-op elsewhere.
//
// PERFORMANCE CONTRACT: nothing here may be driven per-pointermove or per-frame.
// Repeating effects own their own pacing (self-rescheduling setTimeout for the
// rumble, a distance threshold for drag ticks) so callers can safely fire them
// from a rAF loop without flooding the vibration hardware or the iOS toggle.

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

let hapPadInput: HTMLInputElement | null = null;
let hapPadLabel: HTMLLabelElement | null = null;
let hapPadRefCount = 0;

/** Visually-hidden but NOT display:none — some platforms suppress the native
 * switch-toggle Taptic tick on elements removed from layout entirely. */
function hideVisually(el: HTMLElement): void {
  el.style.position = 'fixed';
  el.style.width = '1px';
  el.style.height = '1px';
  el.style.margin = '-1px';
  el.style.padding = '0';
  el.style.border = '0';
  el.style.overflow = 'hidden';
  el.style.clip = 'rect(0, 0, 0, 0)';
  el.style.whiteSpace = 'nowrap';
  el.style.opacity = '0';
  el.style.pointerEvents = 'none';
}

/** Ref-counted: the shared pad node is only removed once every caller has
 *  disposed. Returns the dispose callback — call it on unmount. */
export function initHapticsPad(): () => void {
  if (typeof document === 'undefined') return () => {};

  hapPadRefCount += 1;
  if (!hapPadInput) {
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'hapPad';
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    // Non-standard, WebKit-only: the switch appearance is what fires the tick.
    input.style.setProperty('-webkit-appearance', 'checkbox');
    hideVisually(input);
    hideVisually(label);
    label.appendChild(input);
    document.body.appendChild(label);
    hapPadInput = input;
    hapPadLabel = label;
  }

  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    hapPadRefCount = Math.max(0, hapPadRefCount - 1);
    if (hapPadRefCount === 0 && hapPadLabel) {
      hapPadLabel.remove();
      hapPadInput = null;
      hapPadLabel = null;
    }
  };
}

function tickHapPad(): void {
  // Lazy-create for a buzz that beats mount (e.g. a very early keystroke).
  if (!hapPadInput && typeof document !== 'undefined') {
    initHapticsPad();
  }
  hapPadInput?.click();
}

const supportsVibrate =
  typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

/** `pattern` is ignored on the iOS fallback path — it can only fire one tick. */
export function buzz(pattern: number | number[]): void {
  if (typeof window === 'undefined') return;
  if (supportsVibrate) {
    try {
      navigator.vibrate(pattern);
      return;
    } catch {
      // fall through to the iOS fallback
    }
  }
  tickHapPad();
}

/** Lightest tick — keystrokes, drag ticks, delegated button taps. */
export const hapMicro = (): void => buzz(8);

/** Firmer tick — grabbing the carousel / starting a drag gesture. */
export const hapGrab = (): void => buzz(14);

/** Double-pulse thud — the intro burst / big conversational beats. */
export const hapThud = (): void => buzz([18, 30, 12]);

/** Settle tick — a carousel page (or the spring) coming to rest. */
export const hapSettle = (): void => buzz(12);

export type ChargeRumbleHandle = {
  start: () => void;
  stop: () => void;
};

/**
 * Escalating hold-to-charge rumble, ~130ms tightening to ~35ms as progress
 * hits 1. Self-paced (not ticked from the caller's rAF) — see the performance
 * contract at the top of this file.
 */
export function createChargeRumble(
  getProgress: () => number,
): ChargeRumbleHandle {
  let running = false;
  let timer: number | null = null;

  const tick = () => {
    if (!running) return;
    const p = clamp(getProgress(), 0, 1);
    buzz(Math.round(6 + p * 10));
    const delay = 130 - p * 95;
    timer = window.setTimeout(tick, Math.max(35, delay));
  };

  return {
    start() {
      if (running) return;
      running = true;
      tick();
    },
    stop() {
      running = false;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    },
  };
}

export type DragTickerHandle = {
  /** Call once per gesture start. */
  reset: () => void;
  /** Call with the cumulative drag delta (px) from the gesture's start. */
  report: (dxFromStart: number) => void;
};

/**
 * Fires `hapMicro()` every `stepFraction` of a carousel page, by distance
 * threshold not by event — safe to call from a pointermove handler every frame.
 */
export function createDragTicker(
  pagePx: number,
  stepFraction = 0.035,
): DragTickerHandle {
  const stepPx = Math.max(1, pagePx * stepFraction);
  let lastStep = 0;

  return {
    reset() {
      lastStep = 0;
    },
    report(dxFromStart: number) {
      const step = Math.floor(Math.abs(dxFromStart) / stepPx);
      if (step !== lastStep) {
        lastStep = step;
        hapMicro();
      }
    },
  };
}

const HAPTIC_TAP_ATTR = 'data-haptic-tap';

/**
 * One document-level `pointerdown` listener for every element carrying
 * `data-haptic-tap` (set by the CTA / Ghost components in styles.tsx).
 * Returns the dispose callback — call it on unmount.
 */
export function installDelegatedTapHaptics(): () => void {
  if (typeof document === 'undefined') return () => {};

  const onPointerDown = (e: PointerEvent) => {
    const target = e.target;
    if (target instanceof Element && target.closest(`[${HAPTIC_TAP_ATTR}]`)) {
      hapMicro();
    }
  };

  document.addEventListener('pointerdown', onPointerDown);
  return () => document.removeEventListener('pointerdown', onPointerDown);
}
